/* eslint-disable multiline-comment-style */
/* eslint-disable radix */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { getI18nTime, replaceMoneySymbol } from '@/common/utils/i18nFuncs'
import CommonTable from '@/components/CommonTable'
import TipText from '@/components/TipText'
import { fetchFundFlows } from '../../utils/api'
import { IFundFlowsQueryParams, IFundTransactionItem, IFundTransactionListRes } from '../../utils/types'
import { TransactionFilter } from './components/TransactionFilter'
import styles from './index.module.less'
import { TransactionTypeEnum } from '@/common/utils/consts'
import { getDateTimePattern, getYearMonthPattern } from '@/common/utils/i18n'

interface IFundTransactionsProps {
  visible: boolean;
  updateEndTime?: number;
}

export const FundTransactions: React.FC<IFundTransactionsProps> = ({ visible, updateEndTime }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 20 })
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const latestRequestIdRef = useRef(0)
  const [tableData, setTableData] = useState<IFundTransactionListRes>({
    pageNo: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    result: [],
  })

  // 变动类型枚举映射
  const getOperatorSceneText = (operatorScene: string, record: IFundTransactionItem) => {
    const getBillingName = (record: IFundTransactionItem) => getI18nTime(record?.billInfo?.startDate, getYearMonthPattern())
    const sceneMap: Record<string, string> = {
      'staff_order': t('Keeta_EE_Place_an_order_employee_pdTY', '员工下单'),
      'staff_refund': t('Keeta_EE_employees_refunds_s4Si', '员工退款'),
      // 充值回款
      'after_settle_ent_recharge': record?.billInfo?.startDate && t('Keeta_EE_business_money_back_dhkZ', { billingName: getBillingName(record) }) || '--',
      'precharge_ent_recharge': t('Keeta_EE_recharge_enterprise_vQMr', '充值（企业充值）'),
      // 充值返点：账单出账后 Keeta 给企业的返点转入企业账户
      'rebate_recharge': record?.billInfo?.startDate && t('Keeta_EE_billname_rebate_VMoU', { billingName: getBillingName(record) }) || '--',
      'adjust_bill': t('Keeta_EE_Adjustments_billing_T4Kq', '账单调整'),
      'settle_pay': t('Keeta_EE_Deductions_service_charges_tJJ4', { billingName: getBillingName(record) }), // 服务费扣款
      'settle_refund': t('Keeta_EE_Administration_elimination_Szj2', { billingName: getBillingName(record) }), // 冲销扣款,NOTE: billNo是传billName没错,翻译字段定义错了而已
      // 冲销返点：返点入账错误后通过冲销方式还原数据
      'rebate_reverse': record?.billInfo?.startDate && t('Keeta_EE_Elimination_rebate_f7Wg', { billingName: getBillingName(record) }) || '--',
      'withdraw': t('Keeta_EE_Cash_withdrawal_fqu7', '提现'),
      'credit_adjust': t('Keeta_EE_Adjustment_crediting_dU9a', '授信调整'),
      'temp_credit_adjust': t('Keeta_EE_Adjustment_crediting_2287', '临时授信调整'),
      'reverse_recharge': t('Keeta_EE_reversal_recharge_Y8ck', '充值冲销'),
    }
    return sceneMap[operatorScene] || operatorScene
  }

  // 根据 updateEndTime 计算近三个月的时间范围
  const getThreeMonthsRange = () => {
    const endTime = dayjs(updateEndTime)
    const startTime = endTime.subtract(3, 'months')
    return [startTime.valueOf(), endTime.valueOf()]
  }


  const [startTime, endTime] = updateEndTime ? getThreeMonthsRange() : []
  // TableNew 在 data 已变为空数组时偶现旧行残留，这里用 key 强制重挂载规避组件内部渲染缓存问题。
  const tableRenderKey = [
    pageInfo.pageNo,
    pageInfo.pageSize,
    tableData.totalCount,
    tableData.result.map(item => item.id).join(','),
  ].join('_')
  const normalizeTableData = (params: IFundFlowsQueryParams, res?: IFundTransactionListRes): IFundTransactionListRes => ({
    pageNo: res?.pageNo ?? params.pageNo ?? 1,
    pageSize: res?.pageSize ?? params.pageSize ?? 20,
    totalCount: res?.totalCount ?? 0,
    totalPages: res?.totalPages ?? 0,
    result: Array.isArray(res?.result) ? res.result : [],
  })

  const { loading, runAsync } = useRequest((params: IFundFlowsQueryParams) => fetchFundFlows(params), { manual: true })

  useEffect(() => {
    if (!visible || !updateEndTime) {
      setTableData({
        pageNo: pageInfo.pageNo,
        pageSize: pageInfo.pageSize,
        totalCount: 0,
        totalPages: 0,
        result: [],
      })
      return
    }

    const currentRequestId = latestRequestIdRef.current + 1
    latestRequestIdRef.current = currentRequestId

    const params: IFundFlowsQueryParams = {
      ...pageInfo,
      startTime,
      endTime,
      ...(selectedTypes.length ? { transactionTypeList: selectedTypes.join(',') } : {}),
    }

    runAsync(params)
      .then((res) => {
        if (latestRequestIdRef.current !== currentRequestId) return
        setTableData(normalizeTableData(params, res))
      })
      .catch(() => {
        if (latestRequestIdRef.current !== currentRequestId) return
        setTableData(normalizeTableData(params))
      })
  }, [pageInfo, visible, updateEndTime, selectedTypes, startTime, endTime, runAsync])

  const handleTypeChange = (types: string[]) => {
    setSelectedTypes(types)
    // 筛选条件变更时重置到第 1 页
    setPageInfo(prev => ({ ...prev, pageNo: 1 }))
  }

  const columns = [
    {
      title: t('Keeta_EE_Time_change_yRIw', '变动时间'),
      dataIndex: 'gmtCreated',
      width: 180,
      render: (time: number) => getI18nTime(time, getDateTimePattern()),
    },
    {
      title: t('Keeta_EE_Reference_No_OD5u', '参考编号'),
      dataIndex: 'displayNo',
      width: 200,
      render: (text: string, record: IFundTransactionItem) => {
        const transactionType = typeof record.transactionType === 'string' ? parseInt(record.transactionType) : record.transactionType
        const isOrderType = [TransactionTypeEnum.PAY, TransactionTypeEnum.REFUND].includes(transactionType)

        return (
          <div className={styles.referenceCell}>
            <TipText text={text} style={{ width: 'auto' }} />
            {isOrderType && (
              <span
                className={styles.detailLink}
                onClick={() => navigate(`/order?bizOrderId=${text}`)}
              >
                {t('e_pc_order_details', '订单详情')}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: t('Keeta_EE_changes_types_uyMf', '变动类型'),
      dataIndex: 'operatorScene',
      width: 150,
      render: (operatorScene: string, record: IFundTransactionItem) => {
        const text = getOperatorSceneText(operatorScene, record)
        return text ? <TipText text={text} /> : '--'
      },
    },
    {
      title: t('Keeta_EE_Change_amount_RhsF', '变动金额'),
      dataIndex: 'transactionAmount',
      width: 150,
      render: (_: any, record: IFundTransactionItem) => {
        const amount = record.transactionAmount
        const rawText = replaceMoneySymbol(amount?.displayText || '--')

        if (rawText === '--') {
          return <span>--</span>
        }

        return <span>{rawText}</span>
      },
    },
  ]

  return (
    <div className={styles.transactions}>
      <div className={styles.header}>
        <div className={styles.title}>{t('Keeta_EE_Details_changes_BQ0c', '资金变动明细')}</div>
        <div className={styles.tip}>{t('Keeta_EE_Details_changes_RU76', '仅展示近3个月的变动明细')}</div>
      </div>
      <TransactionFilter
        selectedTypes={selectedTypes}
        onTypeChange={handleTypeChange}
        queryParams={{
          ...pageInfo,
          ...(updateEndTime ? { startTime, endTime } : {}),
          ...(selectedTypes.length ? { transactionTypeList: selectedTypes.join(',') } : {}),
        }}
      />
      <CommonTable
        rowKey="bizOrderId"
        columns={columns}
        data={tableData.result}
        loading={loading}
        pagination={{
          total: tableData.totalCount,
          currentPage: pageInfo.pageNo,
          pageSize: pageInfo.pageSize,
          onChange: (pageNo: number, pageSize: number) => setPageInfo({ pageNo, pageSize }),
        }}
      />
    </div>
  )
}

export default FundTransactions
